import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

/** ✅ Main Sections (Homepage) */
import BBFHeroBanner from "./components/BBFHeroBanner";
import BBFTheReckoningAnimated from "./components/BBFTheReckoningAnimated";
import BBFTheReckoning from "./components/BBFTheReckoning";
import BBFIntro from "./components/BBFIntro";
import BBFTheMoment from "./components/BBFTheMoment";
import BBFTheSetup from "./components/BBFTheSetup";
import BBFTheSnare from "./components/BBFTheSnare";
import BBFNoThereThere from "./components/BBFNoThereThere";
import BBFOurCounteroffensiveBegins from "./components/BBFOurCounteroffensiveBegins";
import BBFBehindTheBrief from "./components/BBFBehindTheBrief";
import BBFDownloadNow from "./components/BBFDownloadNow";
import BBFFooter from "./components/BBFFooter";

/** ✅ Popups */
import BBFExitIntentModal from "@/components/BBFExitIntentModal";
import BBFLeadCaptureModal from "@/components/BBFLeadCaptureModal";
import BBFSalesModalKohberger from "@/components/BBFSalesModalKohberger";

/** ✅ Hooks */
import { useExitIntentLeadPopup } from "@/lib/useExitIntentLeadPopup";
import { useTimerLeadPopup } from "@/lib/useTimerLeadPopup";
import { useScrollSalesPopup } from "@/lib/useScrollSalesPopup";

/** ✅ Popup Controller Context */
import { PopupProvider, usePopupController } from "@/lib/usePopupController";

import PressHub from "./pages/press/index.jsx";
import ButlerBullseye from "./pages/press/001-butler-bullseye";
import YearOf23 from "./pages/press/002-year-of-23";

<Routes>
  <Route path="/press" element={<PressHub />} />
  <Route path="/press/001-butler-bullseye" element={<ButlerBullseye />} />
  <Route path="/press/002-year-of-23" element={<YearOf23 />} />
</Routes>

/* ------------------------- Homepage Content ------------------------- */
function AppContent() {
  /** ✅ DEVELOPMENT RESET (clear popup state on page load) */
  useEffect(() => {
    // console.log("🔄 Resetting popup state on page load...");
    localStorage.removeItem("kohbergerPopupDismissed");
    localStorage.removeItem("leadCaptured");
    sessionStorage.clear();
  }, []);

  /** ✅ Analytics & User Tracking */
  useEffect(() => {
    const getOrGenerateUserId = () => {
      const stored = localStorage.getItem("bbf_user_id");
      if (stored) return stored;
      const newId = "uid_" + Math.random().toString(36).substring(2);
      localStorage.setItem("bbf_user_id", newId);
      return newId;
    };
    const userId = getOrGenerateUserId();

    if (typeof window.gtag === "function") {
      window.gtag("set", "debug_mode", true);
      window.gtag("config", "G-4MQ931G1T5", { user_id: userId });
      // console.log("✅ GA configured with userId:", userId);
    } else {
      // console.log("ℹ️ GA not initialized (likely DEV)");
    }
  }, []);

  /** ✅ Scroll to #hash on load (for email links) */
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const targetId = hash.replace("#", "");
      const element = document.getElementById(targetId);
      if (element) {
        setTimeout(() => element.scrollIntoView({ behavior: "smooth" }), 500);
      } else {
        // console.warn(`⚠️ No element found for hash: ${hash}`);
      }
    }
  }, []);

  /** ✅ Engagement Logic */
  const isDev = import.meta.env.MODE?.toLowerCase().startsWith("dev");

  // ACTIVE: Timer-based popup
  const timerPopup = useTimerLeadPopup({ delaySeconds: isDev ? 3 : 60 });

  // ACTIVE: Scroll-based popup
  const scrollPopup = useScrollSalesPopup();

  // ACTIVE: Exit Intent popup
  const exitPopup = useExitIntentLeadPopup({ enabled: true });

  /** ✅ Central Popup Controller */
  const { leadCaptured, markLeadCaptured } = usePopupController();
  const shouldShow = (flag) => !leadCaptured && flag;

  return (
    <>
      {/* Popups appear on home route only */}
      <BBFExitIntentModal
        show={shouldShow(exitPopup.show)}
        setShow={exitPopup.setShow}
        markLeadCaptured={markLeadCaptured}
      />

      <BBFLeadCaptureModal
        show={shouldShow(timerPopup.show)}
        setShow={timerPopup.setShow}
        markLeadCaptured={markLeadCaptured}
      />

      <BBFSalesModalKohberger
        show={scrollPopup.show} // Always independent
        setShow={scrollPopup.setShow}
        markLeadCaptured={markLeadCaptured}
      />

      {/* Main Page Sections */}
      <BBFHeroBanner />
      <BBFTheReckoningAnimated />
      <BBFTheReckoning />
      <BBFTheMoment />

      {/* Add wrapper with ID for first trigger */}
      <section id="BBFIntro">
        <BBFIntro />
      </section>

      <BBFTheSetup />
      <BBFTheSnare />

      {/* Add wrappers with IDs for scroll triggers */}
      <section id="BBFNoThereThere">
        <BBFNoThereThere />
      </section>

      <section id="BBFOurCounteroffensiveBegins">
        <BBFOurCounteroffensiveBegins />
      </section>

      <BBFBehindTheBrief />
      <BBFDownloadNow />
      <BBFFooter />
    </>
  );
}

/* ------------------------------ App Shell ------------------------------ */
export default function App() {
  return (
    <Router>
      <PopupProvider>
        <Routes>
          {/* Home */}
          <Route path="/" element={<AppContent />} />

          {/* Press Hub + Articles */}
          <Route path="/press" element={<PressHub />} />
          <Route path="/press/001-butler-bullseye" element={<ButlerBullseye />} />
          <Route path="/press/002-year-of-23" element={<YearOf23 />} />

          {/* (Optional) catch-all → home */}
          <Route path="*" element={<AppContent />} />
        </Routes>
      </PopupProvider>
    </Router>
  );
}
